import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";
import { router, useLocalSearchParams, usePathname } from "expo-router";
import type { VehicleFilters } from "@visioni/a3s-vehicle-tracker";
import {
  filtersToRouteParams,
  filtersToUrlParams,
  paramsToFilters,
  getDefaultFilters,
  filterSignature,
} from "@visioni/a3s-vehicle-tracker/app";

const DEBOUNCE_MS = 400;

export interface UseVehicleFiltersReturn {
  /** Immediate draft state for binding to inputs (no debounce lag). */
  filters: VehicleFilters;
  /** Committed filters derived from route params — use for queries. */
  committedFilters: VehicleFilters;
  updateFilter: <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) => void;
  setFilters: (filters: VehicleFilters) => void;
  resetFilters: () => void;
}

/**
 * Unified vehicle filter hook backed by Expo Router's route params.
 *
 * - Exposes a `filters` draft that updates immediately for snappy UI.
 * - Debounces commits to route params (400ms) so queries and URL stay in sync.
 * - Uses `router.setParams` (not `router.replace`) to avoid remounting the
 *   component tree, which would close dropdowns and filter panels.
 * - Works identically on web (URL bar) and native (navigation params).
 */
export function useVehicleFilters(): UseVehicleFiltersReturn {
  const params = useLocalSearchParams();
  const pathname = usePathname();

  // Stable defaults computed once per mount — avoids jitter from Date.now() drift.
  const defaultsRef = useRef<VehicleFilters>(getDefaultFilters());

  // Track the signature of what we last committed so we can ignore the echo
  // from useLocalSearchParams when it round-trips our own setParams call.
  const lastCommittedSigRef = useRef<string | null>(null);

  const committedFilters = useMemo<VehicleFilters>(() => {
    const partial = paramsToFilters(params);
    if (Object.keys(partial).length === 0) return defaultsRef.current;
    return { ...defaultsRef.current, ...partial };
  }, [params]);

  const [draft, setDraft] = useState<VehicleFilters>(committedFilters);

  // Sync draft when route params change externally (e.g. navigation, deep link).
  // Skip when the change came from our own setParams to avoid clobbering the draft.
  const prevCommittedSigRef = useRef(filterSignature(committedFilters));
  useEffect(() => {
    const sig = filterSignature(committedFilters);
    if (sig === prevCommittedSigRef.current) return;
    prevCommittedSigRef.current = sig;

    if (sig === lastCommittedSigRef.current) {
      // This is our own round-trip echo — don't overwrite draft.
      return;
    }
    setDraft(committedFilters);
  }, [committedFilters]);

  // Debounced commit to route params.
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commitToRoute = useCallback((filters: VehicleFilters) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      // Record the signature before committing so the sync effect ignores the echo.
      lastCommittedSigRef.current = filterSignature(filters);
      // setParams shallow-merges — filtersToRouteParams emits ALL keys
      // (empty string for cleared values) so stale params are overwritten.
      router.setParams(filtersToRouteParams(filters));
    }, DEBOUNCE_MS);
  }, []);

  // Commit without debounce for discrete actions (e.g. reset).
  // Debouncing only makes sense for keystroke-driven text inputs; a single
  // button press should update the query and URL immediately.
  const commitToRouteNow = useCallback((filters: VehicleFilters) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    lastCommittedSigRef.current = filterSignature(filters);
    router.setParams(filtersToRouteParams(filters));
  }, []);

  // Commit by pushing a new history entry so the browser Back button returns
  // to the previous filter set. Used for discrete navigation-like actions
  // ("find similar") where the user expects Back to undo the action.
  // On native, pushing the same route duplicates the screen on the stack, so
  // fall back to setParams which keeps the single-screen behaviour.
  const commitToRoutePushing = useCallback(
    (filters: VehicleFilters) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (Platform.OS === "web") {
        const filterParams = filtersToUrlParams(filters);
        // Record the signature of what will round-trip through the URL so the
        // echo-guard matches. filtersToUrlParams omits values that don't meet
        // thresholds (e.g. plateText < 4 chars), so the decoded result differs
        // from the raw input.
        const roundTripped: VehicleFilters = { ...defaultsRef.current, ...paramsToFilters(filterParams) };
        lastCommittedSigRef.current = filterSignature(roundTripped);
        const filterKeys = new Set(Object.keys(filtersToRouteParams(filters)));
        const merged = new URLSearchParams(filterParams);
        for (const [key, value] of Object.entries(params)) {
          if (filterKeys.has(key)) continue;
          const v = Array.isArray(value) ? value[0] : value;
          if (v) merged.set(key, v);
        }
        const query = merged.toString();
        const href = query ? `${pathname}?${query}` : pathname;
        router.push(href as never);
      } else {
        lastCommittedSigRef.current = filterSignature(filters);
        router.setParams(filtersToRouteParams(filters));
      }
    },
    [pathname, params],
  );

  const updateFilter = useCallback(
    <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) => {
      setDraft((prev) => {
        const next = { ...prev, [key]: value };
        commitToRoute(next);
        return next;
      });
    },
    [commitToRoute],
  );

  const setFilters = useCallback(
    (filters: VehicleFilters) => {
      setDraft(filters);
      commitToRoutePushing(filters);
    },
    [commitToRoutePushing],
  );

  const resetFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    defaultsRef.current = defaults;
    setDraft(defaults);
    commitToRouteNow(defaults);
  }, [commitToRouteNow]);

  return { filters: draft, committedFilters, updateFilter, setFilters, resetFilters };
}
